import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { socket } from "../../services/websocket";
import { getAuthJson } from "../../utils/authStorage";
import StructureReimbSyncFailReportModal from "./StructureReimbSyncFailReportModal";

/**
 * Listens for reimbursement structure sync progress/done (any page).
 * Progress chip while running; failure table modal (or short success) when done.
 */
export default function StructureReimbSyncNotifier() {
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(null);
  const myUserId = useRef(null);

  useEffect(() => {
    const u = getAuthJson("currentUser");
    myUserId.current = u?.id != null ? Number(u.id) : null;
  }, []);

  useEffect(() => {
    const onProgress = (payload) => {
      const job = payload?.job;
      if (!job) return;
      const uid = myUserId.current;
      if (uid != null && job.userId != null && Number(job.userId) !== uid) return;
      setProgress(job);
      if (job.status === "completed" || job.status === "failed") {
        setDone(job);
        setProgress(null);
      }
    };
    const onDone = (payload) => {
      const job = payload?.job;
      if (!job) return;
      const uid = myUserId.current;
      if (uid != null && job.userId != null && Number(job.userId) !== uid) return;
      setProgress(null);
      setDone(job);
    };
    socket.on("structure-reimb-sync-progress", onProgress);
    socket.on("structure-reimb-sync-done", onDone);
    return () => {
      socket.off("structure-reimb-sync-progress", onProgress);
      socket.off("structure-reimb-sync-done", onDone);
    };
  }, []);

  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((Number(progress.done || 0) / Number(progress.total)) * 100))
      : 0;

  const hasFails = Array.isArray(done?.errors) && done.errors.length > 0;

  return (
    <>
      {progress && progress.status === "running"
        ? createPortal(
            <div className="fixed bottom-4 right-4 z-[12000] w-80 rounded-sm border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[hsl(var(--ds-fg))]">
                <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--ds-primary))]" />
                Syncing salary structures
              </div>
              <p className="mb-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                {progress.message ||
                  `${progress.done || 0} / ${progress.total || 0} employees`}
              </p>
              <div className="h-2 overflow-hidden rounded-sm bg-[hsl(var(--ds-muted))]">
                <div
                  className="h-full rounded-sm bg-[hsl(var(--ds-primary))] transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="mt-1 text-right text-[10px] text-[hsl(var(--ds-muted-fg))]">{pct}%</p>
            </div>,
            document.body
          )
        : null}

      {done && hasFails ? (
        <StructureReimbSyncFailReportModal
          open
          title={
            done.failed > 0
              ? `Salary structure sync — ${done.failed} failed`
              : "Structure sync — failed employees"
          }
          meta={{
            updated: done.updated,
            skipped: done.skipped,
            failed: done.failed,
            effectiveFrom: done.effectiveFrom,
            total: done.total,
            failedSummary: done.failedSummary,
          }}
          errors={done.errors}
          onClose={() => setDone(null)}
        />
      ) : null}

      {done && !hasFails
        ? createPortal(
            <div className="fixed inset-0 z-[12010] flex items-center justify-center bg-black/45 p-4">
              <div className="w-full max-w-md rounded-sm border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-[var(--ds-shadow-card)]">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">
                      Salary structure updated
                    </h3>
                    <p className="mt-1 text-sm text-[hsl(var(--ds-muted-fg))]">
                      {done.message ||
                        `Updated ${done.updated || 0} of ${done.total || 0} employees.`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDone(null)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <ul className="mb-4 space-y-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                  <li>Effective from: {done.effectiveFrom || "—"}</li>
                  <li>Updated: {done.updated ?? 0}</li>
                  <li>Already current (skipped): {done.skipped ?? 0}</li>
                  <li>Failed: {done.failed ?? 0}</li>
                </ul>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDone(null)}
                    className="rounded-sm bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-medium text-white"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
